import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { BookOpen, Link2, Copy, Eye, Power, KeyRound } from "lucide-react";
import { LinkPinDialog } from "@/components/master-catalog/LinkPinDialog";
import { CustomPriceLists, NewPriceListDialog, PriceListChartDialog } from "@/components/master-catalog/PriceListEditor";

const TYPES = { potential_franchise: "Potential franchise", franchise: "Franchise", potential_distributor: "Potential distributor", distributor: "Distributor", wholesaler: "Wholesaler" };
const ADD_NEW = "__add_new__";

// CS 22.14 -- Wholesaler links pick the price list that prices the whole catalog.
function PriceListSelect({ value, onChange, options, onAddNew }) {
  return (
    <div className="space-y-1" data-testid="mc-link-pricelist-wrap">
      <Select value={value} onValueChange={(v) => (v === ADD_NEW ? onAddNew() : onChange(v))}>
        <SelectTrigger data-testid="mc-link-pricelist-select"><SelectValue placeholder="Select price list" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={`${o.kind}:${o.id}`} value={`${o.kind}:${o.id}`} data-testid={`mc-pl-opt-${o.id}`}>
              {o.name} · {o.cost_currency === o.selling_currency ? o.cost_currency : `${o.cost_currency}/${o.selling_currency}`}{o.kind === "custom" ? " · custom" : ""}
            </SelectItem>
          ))}
          <SelectItem value={ADD_NEW} data-testid="mc-pl-opt-add-new">+ Add new custom pricing</SelectItem>
        </SelectContent>
      </Select>
      {options.length === 0 && <p className="text-[11px] text-slate-500" data-testid="mc-pl-empty-hint">No wholesalers or price lists yet. Add a custom price list to continue.</p>}
    </div>
  );
}
const linkUrl = (t) => `${window.location.origin}/mc/${t}`;

function CreateLinkDialog({ open, onOpenChange, onCreated, onPriceListCreated }) {
  const [f, setF] = useState({ name: "", partner_type: "franchise", partner_franchise_id: "none", pin: "", price_list: "" });
  const [franchises, setFranchises] = useState([]);
  const [plOptions, setPlOptions] = useState([]);
  const [newPlOpen, setNewPlOpen] = useState(false);
  const [chartPl, setChartPl] = useState(null);
  const loadPl = () => api.get("/wholesale-price-lists/options").then((r) => setPlOptions(r.data.items));
  useEffect(() => { if (open) api.get("/franchises").then((r) => setFranchises(r.data.filter((x) => x.is_active))); }, [open]);
  useEffect(() => { if (open && f.partner_type === "wholesaler") loadPl(); }, [open, f.partner_type]);
  const save = async () => {
    try {
      const [plKind, ...plId] = (f.price_list || "").split(":");
      const body = { ...f, partner_franchise_id: f.partner_type === "franchise" && f.partner_franchise_id !== "none" ? f.partner_franchise_id : null,
        price_list: f.partner_type === "wholesaler" ? { kind: plKind, id: plId.join(":") } : null };
      const { data } = await api.post("/master-catalog/links", body);
      toast.success(`Link "${data.name}" created`);
      setF({ name: "", partner_type: "franchise", partner_franchise_id: "none", pin: "", price_list: "" });
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const valid = f.name.trim() && /^\d{4,6}$/.test(f.pin) && (f.partner_type !== "wholesaler" || f.price_list);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="mc-create-link-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Create Selection Link</DialogTitle></DialogHeader>
        <DialogDescription>One named link per partner. A 4-6 digit PIN gates the link; PINs can be reused across links.</DialogDescription>
        <div className="space-y-3">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Partner name (e.g. Revankar Jewellers)" data-testid="mc-link-name-input" />
          <Select value={f.partner_type} onValueChange={(v) => setF({ ...f, partner_type: v, price_list: "" })}>
            <SelectTrigger data-testid="mc-link-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {f.partner_type === "franchise" && (
            <Select value={f.partner_franchise_id} onValueChange={(v) => setF({ ...f, partner_franchise_id: v })}>
              <SelectTrigger data-testid="mc-link-franchise-select"><SelectValue placeholder="Franchise (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No franchise association</SelectItem>
                {franchises.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {f.partner_type === "wholesaler" && (
            <PriceListSelect value={f.price_list} options={plOptions} onChange={(v) => setF({ ...f, price_list: v })}
              onAddNew={() => setNewPlOpen(true)} />
          )}
          <Input value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} inputMode="numeric"
            placeholder="PIN (4–6 digits)" className="tracking-[0.3em]" data-testid="mc-link-pin-input" />
        </div>
        <NewPriceListDialog open={newPlOpen} onOpenChange={setNewPlOpen}
          onCreated={(pl) => { setNewPlOpen(false); loadPl(); onPriceListCreated?.(); setF((x) => ({ ...x, price_list: `custom:${pl.id}` })); setChartPl(pl); }} />
        <PriceListChartDialog pl={chartPl} onClose={() => setChartPl(null)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={!valid} className="bg-[#0D1B2A] text-white" data-testid="mc-link-create-submit">Create link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MasterCatalogHome() {
  const nav = useNavigate();
  const [links, setLinks] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [pinLink, setPinLink] = useState(null);
  const [plKey, setPlKey] = useState(0);
  const load = () => api.get("/master-catalog/links").then((r) => setLinks(r.data.items));
  useEffect(() => { load(); }, []);
  const toggle = async (l) => {
    await api.patch(`/master-catalog/links/${l.id}`, { is_active: !l.is_active });
    load();
  };
  return (
    <div className="space-y-8" data-testid="mc-home">
      <div>
        <h1 className="font-serif text-4xl text-[#0D1B2A]">Master Catalog</h1>
        <p className="text-sm text-slate-500 mt-1">One card per Style No design across all stock, designs, Ashth Lite, wholesaler and sold history. Priced live on the franchise engine.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <button onClick={() => nav("/master-catalog/browse")} className="luxe-card p-6 text-left hover:-translate-y-0.5 transition-transform" data-testid="mc-action-open">
          <BookOpen className="w-6 h-6 text-[#C9A84C]" />
          <div className="font-serif text-2xl mt-3 text-[#0D1B2A]">Open / Preview Master Catalog</div>
          <div className="text-sm text-slate-500 mt-1">Browse as HO. Nothing is saved and no link is created.</div>
        </button>
        <button onClick={() => setCreateOpen(true)} className="luxe-card p-6 text-left hover:-translate-y-0.5 transition-transform" data-testid="mc-action-create-link">
          <Link2 className="w-6 h-6 text-[#C9A84C]" />
          <div className="font-serif text-2xl mt-3 text-[#0D1B2A]">Create Selection Link</div>
          <div className="text-sm text-slate-500 mt-1">A named, PIN-gated link that keeps one partner's selection.</div>
        </button>
      </div>
      <div className="luxe-card p-5 space-y-3" data-testid="mc-links-manager">
        <h2 className="font-serif text-xl text-[#0D1B2A]">Selection links</h2>
        {links.length === 0 && <div className="text-sm text-slate-400" data-testid="mc-links-empty">No links yet.</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <tbody>
              {links.map((l) => (
                <tr key={l.id} className="border-t border-[#0D1B2A]/5" data-testid={`mc-link-row-${l.id}`}>
                  <td className="py-2 font-medium">{l.name}</td>
                  <td className="text-xs text-slate-500" data-testid={`mc-link-type-${l.id}`}>{TYPES[l.partner_type]}{l.price_list ? ` · ${l.price_list.name} (${l.price_list.cost_currency})` : ""}</td>
                  <td className="text-xs">{l.line_count} line(s)</td>
                  <td className="text-xs">{l.is_active ? <span className="text-emerald-700">Active</span> : <span className="text-slate-400">Disabled</span>}</td>
                  <td className="text-[11px] text-slate-400" data-testid={`mc-link-pin-reset-at-${l.id}`}>{l.last_pin_reset_at ? `PIN reset ${new Date(l.last_pin_reset_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}` : ""}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => { navigator.clipboard?.writeText(linkUrl(l.token)); toast.success("Link copied"); }} className="p-1.5 hover:text-[#C9A84C]" title="Copy link" data-testid={`mc-link-copy-${l.id}`}><Copy className="w-4 h-4" /></button>
                    <button onClick={() => nav(`/master-catalog/links/${l.id}`)} className="p-1.5 hover:text-[#C9A84C]" title="Open selection" data-testid={`mc-link-open-${l.id}`}><Eye className="w-4 h-4" /></button>
                    <button onClick={() => setPinLink(l)} className="p-1.5 hover:text-[#C9A84C]" title="View PIN" data-testid={`mc-link-view-pin-${l.id}`}><KeyRound className="w-4 h-4" /></button>
                    <button onClick={() => toggle(l)} className="p-1.5 hover:text-red-600" title={l.is_active ? "Disable" : "Enable"} data-testid={`mc-link-toggle-${l.id}`}><Power className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CustomPriceLists refreshKey={plKey} />
      <LinkPinDialog link={pinLink} onClose={() => setPinLink(null)} onChanged={load} />
      <CreateLinkDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => { setCreateOpen(false); load(); }}
        onPriceListCreated={() => setPlKey((k) => k + 1)} />
    </div>
  );
}
